import Helpers from "@/helpers/helpers";
import {Roster, User} from "@/api/dto/dto";
import {
  AppRole, DataPermissionLevel, DataPermissionLevelHierarchy,
  DataPermissionLevelNew,
  DataSource, Feature,
  FeatureNew,
  FeaturePermissionLevel,
  FeaturePermissionLevelHierarchy,
  NewDataPermissionLevelHierarchy
} from "@/api/dto/permissions";

const PermissionsHelpers = {
  isBamboo(user: User): boolean {
    if (!user) return false;
    return user?.role === AppRole.BAMBOO_ADMIN;
  },
  isSuperAdmin(user: User): boolean {
    if (!user) return false;
    return user?.role === AppRole.SUPER_ADMIN;
  },
  isOverSeasCareNavigator(user: User): boolean {
    if (!user) return false;
    return user?.role === AppRole.OVERSEAS_CARE_NAVIGATOR;
  },
  isUser(user: User): boolean {
    if (!user) return false;
    return user.role === AppRole.USER;
  },
  isSSOUser(user: User | undefined): boolean {
    if (!user) return false;
    return Helpers.isDefined(user.sso_connection);
  },
  hasAdminRole(user: User): boolean {
    return [AppRole.SUPER_ADMIN, AppRole.BAMBOO_ADMIN].includes(user?.role);
  },
  checkPermissionDataLevel(userLevel: DataPermissionLevel, level: DataPermissionLevel): boolean {
    if (userLevel === level) {
      return true;
    }

    return DataPermissionLevelHierarchy.indexOf(userLevel) > DataPermissionLevelHierarchy.indexOf(level);
  },

  checkPermissionDataLevelNew(userLevel: DataPermissionLevelNew, level: DataPermissionLevelNew): boolean {
    if (userLevel === level) {
      return true;
    }

    return NewDataPermissionLevelHierarchy.indexOf(userLevel) > NewDataPermissionLevelHierarchy.indexOf(level);
  },

  checkPermissionFeatureLevel(userLevel: FeaturePermissionLevel, level: FeaturePermissionLevel): boolean {
    if (userLevel === level) {
      return true;
    }

    return FeaturePermissionLevelHierarchy.indexOf(userLevel) > FeaturePermissionLevelHierarchy.indexOf(level);
  },

  // checks if you can do a feature action for a specific roster
  canForUserFeaturePermissionRoster(user: User, roster: Roster, level: FeaturePermissionLevel, feature: Feature) {
    if (!user || !roster) return false;

    const userLevel = user.effectivePermissions.byRoster[roster.id]?.featurePermissions[feature];

    return !!userLevel && this.checkPermissionFeatureLevel(userLevel, level);
  },

  // checks if you can do a feature action for a specific roster
  canForUserFeaturePermissionRosterNew(user: User, roster: Roster, level: FeaturePermissionLevel, feature: FeatureNew) {
    if (!user || !roster) return false;

    const userLevel = user.effectivePermissions.byRoster[roster.id]?.featurePermissionsNew[feature];

    return !!userLevel && this.checkPermissionFeatureLevel(userLevel, level);
  },

  // checks if you can do a feature action for a specific roster
  canForUserDataPermissionRoster(user: User, roster: Roster, level: DataPermissionLevel, dataSource: DataSource) {
    if (!user || !roster) return false;

    const userLevel = user.effectivePermissions.byRoster[roster.id]?.dataPermissions[dataSource];

    return !!userLevel && this.checkPermissionDataLevel(userLevel, level);
  },

  // checks if you can do a feature action for a specific roster
  canForUserDataPermissionRosterNew(user: User, roster: Roster, level: DataPermissionLevelNew, dataSource: DataSource) {
    if (!user || !roster) return false;

    const userLevel = user.effectivePermissions.byRoster[roster.id]?.dataPermissionsNew[dataSource];

    return !!userLevel && this.checkPermissionDataLevelNew(userLevel, level);
  },

  // checks if you can do an action for any roster
  canForUserFeaturePermission(user: User, level: FeaturePermissionLevel, feature: Feature) {
    if (!user) return false;

    const anyRosterLevel = user.effectivePermissions.anyRoster.featurePermissions[feature];

    return !!anyRosterLevel && this.checkPermissionFeatureLevel(anyRosterLevel, level);
  },

  // checks if you can do an action for any roster
  canForUserFeaturePermissionNew(user: User, level: FeaturePermissionLevel, feature: FeatureNew) {
    if (!user) return false;

    const anyRosterLevel = user.effectivePermissions.anyRoster.featurePermissionsNew[feature];

    return !!anyRosterLevel && this.checkPermissionFeatureLevel(anyRosterLevel, level);
  },

  // checks if you can do an action for any roster
  canForUserDataPermission(user: User, level: DataPermissionLevel, dataSource: DataSource) {
    if (!user) return false;

    const anyRosterLevel = user.effectivePermissions.anyRoster.dataPermissions[dataSource];

    return !!anyRosterLevel && this.checkPermissionDataLevel(anyRosterLevel, level);
  },

  // checks if you can do an action for any roster
  canForUserDataPermissionNew(user: User, level: DataPermissionLevelNew, dataSource: DataSource) {
    if (!user) return false;

    const anyRosterLevel = user.effectivePermissions.anyRoster.dataPermissionsNew[dataSource];

    return !!anyRosterLevel && this.checkPermissionDataLevelNew(anyRosterLevel, level);
  },

  hasDataAccess(roster: Roster, dataSource: DataSource): boolean {
    switch (dataSource) {
    case DataSource.ADT:
      return roster.permissionsNew?.dataPermissions?.adtEnabled;
    case DataSource.OB_REFERRALS:
      return roster.permissionsNew?.dataPermissions?.obReferralsEnabled;
    case DataSource.OB_CRISIS:
      return roster.permissionsNew?.dataPermissions?.obCrisisEnabled;
    case DataSource.PDMP:
      return roster.permissionsNew?.dataPermissions?.pdmpEnabled;
    case DataSource.JI:
      return roster.permissionsNew?.dataPermissions?.jiEnabled;
    case DataSource.FINDHELP:
      return roster.permissionsNew?.dataPermissions?.findHelpEnabled;
    case DataSource.ROUNDTRIP:
      return roster.permissionsNew?.dataPermissions?.roundtripEnabled;
    default:
      return false;
    }
  },
  hasFeatureAccess(roster: Roster, feature: FeatureNew): boolean {
    switch (feature) {
    case FeatureNew.ADMIN_MANAGEMENT:
      return roster.permissionsNew?.featurePermissions?.adminManagementEnabled;
    case FeatureNew.CLIENT_LIST:
      return roster.permissionsNew?.featurePermissions?.clientListEnabled;
    case FeatureNew.FORMS:
      return roster.permissionsNew?.featurePermissions?.formsEnabled;
    case FeatureNew.ANALYTICS:
      return roster.permissionsNew?.featurePermissions?.analyticsEnabled;
    case FeatureNew.TASKS:
      return roster.permissionsNew?.featurePermissions?.tasksEnabled;
    case FeatureNew.REFERRALS:
      return roster.permissionsNew?.featurePermissions?.referralsEnabled;
    case FeatureNew.MY_NETWORK:
      return roster.permissionsNew?.featurePermissions?.myNetworkEnabled;
    case FeatureNew.NETWORK_REGISTRY_BROWSER:
      return roster.permissionsNew?.featurePermissions?.networkRegistryBrowserEnabled;
    case FeatureNew.NETWORK_FACILITIES_UNITS:
      return roster.permissionsNew?.featurePermissions?.networkFacilitiesAndUnitsEnabled;
    default:
      return false;
    }
  },
  hasRosterAccess(user: User, roster: Roster): boolean {
    // Bamboo admins and offshore care navigators have dynamic roster access computed server-side
    // (via overrideRosters). Their user.rosters list is empty since they have no persistent
    // user_roster DB entries, so we bypass the membership check here.
    return [AppRole.BAMBOO_ADMIN, AppRole.OVERSEAS_CARE_NAVIGATOR].includes(user.role)
      || Helpers.isDefined(user.rosters.find(r => r.roster.id === roster.id && r.roster.active));
  }
};

export default PermissionsHelpers;
